import { OracleConversationQuestion } from '@modules/oracle/components/OracleConversationQuestion';
import { OracleConversationResponse } from '@modules/oracle/components/OracleConversationResponse/OracleConversationResponse';
import { WisdomConversationSteps } from '@modules/oracle/components/WisdomConversationSteps';
import { OraclePrompt } from '@modules/oracle/store/types';
import {
  constructMarkdownFromAssistantResponseToken,
  trackWisdomEvents,
} from '@modules/oracle/utils';
import { getMarkdownForChatResponse } from '@modules/oracle/utils/getMarkdownForChatResponse';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useToggle } from 'react-use';

interface OracleSearchConversationProps {
  prompt: OraclePrompt;
  isReadOnly?: boolean;
  onContentLoaded?: () => void;
}

export function OracleSearchConversation({
  prompt,
  isReadOnly = false,
  onContentLoaded,
}: OracleSearchConversationProps) {
  const [shouldShowSteps, toggleShouldShowSteps] = useToggle(true);
  const answerMarkdown = useMemo(
    () =>
      constructMarkdownFromAssistantResponseToken({
        assistantResponseTokenV2List:
          prompt.assistantResponse?.answer?.tokensV2,
        tokenToMarkdownConverter: getMarkdownForChatResponse,
      }),
    [prompt.assistantResponse?.answer?.tokensV2],
  );

  const hasFinalAnswer = !!answerMarkdown;

  const error = prompt.error;

  useEffect(() => {
    if (!error?.message) {
      return;
    }

    trackWisdomEvents({
      eventName: 'Wisdom - Error Occurred',
      searchText: prompt.question,
      error: error?.message,
    });
  }, [error?.message, prompt.question]);

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
      }}
    >
      <Box>
        <Stack>
          <OracleConversationQuestion
            promptText={prompt.question}
            isReadOnly={isReadOnly}
            promptID={prompt.id}
          />
          <Tooltip
            arrow
            placement="right"
            title={shouldShowSteps ? 'Hide steps' : 'View steps'}
          >
            <Box component="span" sx={{ width: 'fit-content' }}>
              <Button
                variant="text"
                size="small"
                sx={{ color: 'text.secondary' }}
                disabled={!hasFinalAnswer}
                endIcon={
                  shouldShowSteps ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                onClick={() => {
                  toggleShouldShowSteps();
                  trackWisdomEvents({
                    eventName: shouldShowSteps
                      ? 'Wisdom - Hide Steps'
                      : 'Wisdom - Show Steps',
                    searchText: prompt.question,
                  });
                }}
              >
                Steps
              </Button>
            </Box>
          </Tooltip>
        </Stack>
        <Collapse in={shouldShowSteps}>
          <WisdomConversationSteps
            isReadOnly={isReadOnly}
            prompt={prompt}
            hasFinalAnswer={hasFinalAnswer}
          />
        </Collapse>
      </Box>
      <OracleConversationResponse
        prompt={prompt}
        isReadOnly={isReadOnly}
        onContentLoaded={onContentLoaded}
        answerMarkdown={answerMarkdown}
      />
    </Stack>
  );
}
